<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { X, ListMinus, ListX, Columns2, Rows2 } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import PanelTabCloseButton from '@/components/PanelTabCloseButton.vue';
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
	ContextMenuTrigger,
} from '@/components/ui/context-menu';
import type {
	Panel,
	LeafNode,
	PanelHandle,
	PanelRegion,
	PanelSystem,
	DropPosition,
	CloseMode,
} from '@/composables/use-panels';

const props = defineProps<{
	node: LeafNode;
	panels: PanelSystem;
	region: PanelRegion;
	autoHideTabs?: boolean;
	// when set, the content area gets this explicit size in px (width for left/right, height for bottom)
	contentSize?: number;
	// true when an active drag could target this container — forces tab bar visible even when empty
	isDropTarget?: boolean;
	// allowed split directions — DockContainer can restrict to vertical only
	allowedSplitDirections?: ('horizontal' | 'vertical')[];
}>();

const emit = defineEmits<{
	tabMouseUp: [uri: string, wasActive: boolean];
}>();

const isSide = computed(() => props.region === 'left' || props.region === 'right');
const currentActiveUri = computed(() => props.panels.activeUriForNode(props.node));

const showTabs = computed(() => {
	if (props.isDropTarget) return true;
	if (props.autoHideTabs && props.node.panels.length <= 1) return false;
	return props.node.panels.length > 0;
});

const contentStyle = computed(() => {
	if (props.contentSize === undefined) return undefined;
	if (isSide.value) return { width: `${props.contentSize}px` };
	return { height: `${props.contentSize}px` };
});

// --- tab interaction ---

// tracks whether the tab was already active when mousedown fired (used by DockContainer to decide minimize toggle)
let wasActiveOnMouseDown = false;

const onTabMouseDown = (panel: Panel) => {
	wasActiveOnMouseDown = panel.uri === currentActiveUri.value;
	props.panels.focus(panel.uri);
};

const onTabMouseUp = (panel: Panel) => {
	emit('tabMouseUp', panel.uri, wasActiveOnMouseDown);
};

const isPrevInactive = (index: number, uri: string): boolean => {
	if (index === 0) return false;
	if (uri === currentActiveUri.value) return false;
	const prev = props.node.panels[index - 1];
	return prev !== undefined && prev.uri !== currentActiveUri.value;
};

const handle = (uri: string): PanelHandle | undefined => props.panels.handles.get(uri);

const displayTitle = (panelTitle: string, uri: string): string => handle(uri)?.title || panelTitle;

// --- tab drag-and-drop (same-node reorder + cross-node insert) ---

// local state — only set when this node is the drag source
const draggingUri = ref<string | null>(null);
const dropIndex = ref<number | null>(null);
// suppresses shift transition on the first frame so collapse + gap appear simultaneously
const settling = ref(false);
// measured flexbox gap — used to cancel the collapsed tab's gap contribution via negative margin
const dragGap = ref(0);

const isLocalDrag = computed(() => draggingUri.value !== null);
const sharedDragUri = computed(() => props.panels.draggingUri.value);
const dragOffset = computed(() => props.panels.draggingOffset.value);

const isCrossDrag = computed(() => {
	const uri = sharedDragUri.value;
	if (!uri || !props.region) return false;
	if (isLocalDrag.value) return false;
	const found = props.panels.findPanel(uri);
	if (!found) return false;
	// cross-drag if the panel is in a different leaf node AND can move to this region
	return found.leaf.id !== props.node.id && props.panels.canMoveToRegion(uri, props.region);
});

const hasActiveDrag = computed(() => isLocalDrag.value || isCrossDrag.value);

const resetDrag = () => {
	draggingUri.value = null;
	dropIndex.value = null;
	settling.value = false;
	dragGap.value = 0;
	dropPosition.value = null;
};

// clean up stale local state when any drag ends
watch(sharedDragUri, (uri) => {
	if (!uri) resetDrag();
});

const onDragStart = (e: DragEvent, panel: Panel) => {
	if (!e.dataTransfer) return;
	const tab = e.currentTarget as HTMLElement;
	const gap = parseFloat(getComputedStyle(tab.parentElement!).columnGap) || 0;
	const index = props.node.panels.findIndex((p) => p.uri === panel.uri);
	e.dataTransfer.effectAllowed = 'move';
	e.dataTransfer.setData('application/x-panel-tab', panel.uri);
	dragGap.value = gap;
	props.panels.draggingUri.value = panel.uri;
	props.panels.draggingOffset.value = tab.offsetWidth + gap;
	// pre-set dropIndex so the gap opens where the tab was — no net movement
	dropIndex.value = index;
	settling.value = true;
	// first rAF: browser captures the drag ghost from the still-visible tab
	// second rAF: transitions enabled after initial layout is committed
	requestAnimationFrame(() => {
		draggingUri.value = panel.uri;
		requestAnimationFrame(() => {
			settling.value = false;
		});
	});
};

const onTabDragOver = (e: DragEvent, overIndex: number) => {
	if (!hasActiveDrag.value || !e.dataTransfer) return;
	// only handle tab-bar-level reordering when not showing split overlay
	if (dropPosition.value) return;
	e.preventDefault();
	e.dataTransfer.dropEffect = 'move';
	const target = (e.currentTarget as HTMLElement).getBoundingClientRect();
	const midpoint = target.left + target.width / 2;
	dropIndex.value = e.clientX > midpoint ? overIndex + 1 : overIndex;
};

const onTabBarDragOver = (e: DragEvent) => {
	if (!hasActiveDrag.value || !e.dataTransfer) return;
	if (dropPosition.value) return;
	e.preventDefault();
	e.dataTransfer.dropEffect = 'move';
	if (props.node.panels.length === 0) {
		dropIndex.value = 0;
	}
};

const onDrop = (e: DragEvent) => {
	e.preventDefault();
	const uri = sharedDragUri.value;
	if (!uri) return;

	// handle split drop (content area drop zone)
	if (dropPosition.value) {
		props.panels.splitNode(uri, props.node.id, dropPosition.value);
		props.panels.draggingUri.value = null;
		return;
	}

	// handle tab bar drop (reorder / move to this node)
	if (isLocalDrag.value) {
		if (dropIndex.value === null) return;
		const fromIndex = props.node.panels.findIndex((p) => p.uri === uri);
		let target = dropIndex.value;
		if (fromIndex < target) target--;
		if (fromIndex !== target) {
			props.panels.movePanel(uri, target);
		}
	} else if (isCrossDrag.value) {
		const target = dropIndex.value ?? props.node.panels.length;
		props.panels.movePanelToNode(uri, props.node.id, target);
	}

	props.panels.draggingUri.value = null;
};

const onDragEnd = () => {
	props.panels.draggingUri.value = null;
};

// shift tabs at [dropIndex, end) right to make room for the incoming tab
const tabShift = (index: number): string | undefined => {
	if (!hasActiveDrag.value || dropIndex.value === null) return undefined;
	if (dropPosition.value) return undefined;
	if (isLocalDrag.value && props.node.panels[index]?.uri === draggingUri.value) return undefined;
	if (index >= dropIndex.value) return `translateX(${dragOffset.value}px)`;
	return undefined;
};

// --- split drop zone overlay ---

const dropPosition = ref<DropPosition | null>(null);
const contentRef = ref<HTMLElement | null>(null);

// compute which zone the cursor is in based on position within the content area
const computeDropPosition = (e: DragEvent): DropPosition => {
	const el = contentRef.value;
	if (!el) return 'center';
	const rect = el.getBoundingClientRect();
	const x = (e.clientX - rect.left) / rect.width;
	const y = (e.clientY - rect.top) / rect.height;

	const allowed = props.allowedSplitDirections;
	const canHorizontal = !allowed || allowed.includes('horizontal');
	const canVertical = !allowed || allowed.includes('vertical');

	// edge threshold — 25% from each edge
	const threshold = 0.25;

	if (canHorizontal && x < threshold) return 'left';
	if (canHorizontal && x > 1 - threshold) return 'right';
	if (canVertical && y < threshold) return 'top';
	if (canVertical && y > 1 - threshold) return 'bottom';
	return 'center';
};

const onContentDragOver = (e: DragEvent) => {
	const uri = sharedDragUri.value;
	if (!uri || !e.dataTransfer) return;
	// only show split zones for cross-node or same-node drags over content
	if (!props.panels.canMoveToRegion(uri, props.region)) return;
	e.preventDefault();
	e.dataTransfer.dropEffect = 'move';
	dropPosition.value = computeDropPosition(e);
};

const onContentDragLeave = (e: DragEvent) => {
	const related = e.relatedTarget as Node | null;
	if (related && (e.currentTarget as HTMLElement).contains(related)) return;
	dropPosition.value = null;
};

// ghost overlay CSS class based on drop position
const ghostClass = computed(() => {
	if (!dropPosition.value) return '';
	const map: Record<DropPosition, string> = {
		left: 'split-ghost-left',
		right: 'split-ghost-right',
		top: 'split-ghost-top',
		bottom: 'split-ghost-bottom',
		center: 'split-ghost-center',
	};
	return map[dropPosition.value];
});

// zone-level drag handlers for cross-node drops (accept on entire container)
const onZoneDragOver = (e: DragEvent) => {
	if (!isCrossDrag.value || !e.dataTransfer) return;
	e.preventDefault();
	e.dataTransfer.dropEffect = 'move';
	if (dropIndex.value === null) {
		dropIndex.value = props.node.panels.length;
	}
};

const onZoneDragLeave = (e: DragEvent) => {
	const related = e.relatedTarget as Node | null;
	if (related && (e.currentTarget as HTMLElement).contains(related)) return;
	dropIndex.value = null;
};

// --- tab context menu ---

const contextPanel = ref<Panel | null>(null);

const onTabContextMenu = (panel: Panel) => {
	contextPanel.value = panel;
};

const hasOtherClosable = computed(() => {
	if (!contextPanel.value) return false;
	return props.node.panels.some((p) => p.uri !== contextPanel.value!.uri && p.closable);
});

const hasAnyClosable = computed(() => props.node.panels.some((p) => p.closable));

const canSplitHorizontal = computed(() => {
	if (!props.allowedSplitDirections) return true;
	return props.allowedSplitDirections.includes('horizontal');
});

const canSplitVertical = computed(() => {
	if (!props.allowedSplitDirections) return true;
	return props.allowedSplitDirections.includes('vertical');
});

const onContextClose = () => {
	if (contextPanel.value) props.panels.close(contextPanel.value.uri);
};

const onContextCloseMany = (mode: CloseMode) => {
	if (contextPanel.value) props.panels.closeMany(contextPanel.value.uri, mode);
};

const onContextSplitRight = () => {
	if (contextPanel.value) {
		props.panels.splitNode(contextPanel.value.uri, props.node.id, 'right');
	}
};

const onContextSplitDown = () => {
	if (contextPanel.value) {
		props.panels.splitNode(contextPanel.value.uri, props.node.id, 'bottom');
	}
};
</script>

<template>
	<div
		:class="[
			'flex overflow-hidden',
			region === 'left' ? 'flex-row' : region === 'right' ? 'flex-row-reverse' : 'flex-col',
			contentSize === undefined ? 'h-full' : '',
			{
				'border border-border rounded-xl': !isSide,
				'gap-2': isSide,
			},
		]"
		@dragover="onZoneDragOver"
		@dragleave="onZoneDragLeave"
		@drop="onDrop"
	>
		<!-- tab bar — v-show to avoid DOM patching race with Teleport when last panel is dragged out -->
		<ContextMenu>
			<ContextMenuTrigger
				as="div"
				v-show="showTabs"
				:class="
					cn('tab-bar flex shrink-0 gap-1.5', {
						'min-w-8 py-1.5 overflow-y-auto': isSide,
						'min-h-11 p-1.5 overflow-x-auto bg-foreground/2': !isSide,
						'border-border border-b': !isSide && (contentSize === undefined || contentSize > 0),
					})
				"
				@dragover="onTabBarDragOver"
				@drop="onDrop"
			>
				<button
					v-for="(panel, index) in node.panels"
					:key="panel.uri"
					draggable="true"
					:class="[
						'tab-pill relative flex shrink-0 h-8 min-w-8 items-center gap-1.5 rounded-md text-sm transition-[color,background-color] duration-150',
						isPrevInactive(index, panel.uri) ? 'has-separator' : '',
						panel.uri === currentActiveUri
							? 'active bg-muted text-foreground'
							: 'inactive text-muted-foreground hover:bg-muted/50 hover:text-foreground',
						isSide ? 'justify-center' : 'px-2.5',
						draggingUri === panel.uri ? 'tab-dragging' : '',
						hasActiveDrag && !settling ? 'tab-shifting' : '',
					]"
					:style="{
						transform: tabShift(index),
						marginInline: draggingUri === panel.uri ? `${-dragGap / 2}px` : undefined,
					}"
					@mousedown="onTabMouseDown(panel)"
					@mouseup="onTabMouseUp(panel)"
					@contextmenu="onTabContextMenu(panel)"
					@dragstart="onDragStart($event, panel)"
					@dragover="onTabDragOver($event, index)"
					@dragend="onDragEnd"
				>
					<component :is="panel.icon" v-if="panel.icon" class="size-4 shrink-0" />

					<span v-if="!isSide" class="max-w-40 truncate">{{
						displayTitle(panel.title, panel.uri)
					}}</span>

					<PanelTabCloseButton
						v-if="panel.closable"
						:panel="panel"
						:handle="handle(panel.uri)!"
						@close="panels.close(panel.uri)"
					/>
				</button>
			</ContextMenuTrigger>

			<ContextMenuContent>
				<ContextMenuItem :disabled="!contextPanel?.closable" @select="onContextClose">
					<X class="mr-2" />
					Close
				</ContextMenuItem>
				<ContextMenuItem :disabled="!hasOtherClosable" @select="onContextCloseMany('others')">
					<ListMinus class="mr-2" />
					Close Others
				</ContextMenuItem>
				<ContextMenuItem :disabled="!hasAnyClosable" @select="onContextCloseMany('all')">
					<ListX class="mr-2" />
					Close All
				</ContextMenuItem>
				<template v-if="canSplitHorizontal || canSplitVertical">
					<ContextMenuSeparator />
					<ContextMenuItem
						v-if="canSplitHorizontal"
						:disabled="node.panels.length < 2"
						@select="onContextSplitRight"
					>
						<Columns2 class="mr-2" />
						Split Right
					</ContextMenuItem>
					<ContextMenuItem
						v-if="canSplitVertical"
						:disabled="node.panels.length < 2"
						@select="onContextSplitDown"
					>
						<Rows2 class="mr-2" />
						Split Down
					</ContextMenuItem>
				</template>
			</ContextMenuContent>
		</ContextMenu>

		<!-- panel content area — panels are teleported here from WorkspaceLayout -->
		<div
			ref="contentRef"
			:id="`panel-content-${node.id}`"
			:class="[
				contentSize === undefined ? 'relative flex-1' : 'relative shrink-0',
				isSide ? 'border border-border rounded-xl bg-editor' : '',
			]"
			:style="contentStyle"
			@dragover="onContentDragOver"
			@dragleave="onContentDragLeave"
			@drop="onDrop"
		>
			<!-- v-show to avoid DOM patching race with Teleport when last panel is moved out -->
			<div
				v-show="node.panels.length === 0"
				class="flex h-full items-center justify-center text-sm text-muted-foreground"
			>
				<slot name="empty" />
			</div>

			<!-- split ghost overlay -->
			<div v-if="dropPosition" :class="['split-ghost', ghostClass]" />
		</div>
	</div>
</template>

<style scoped>
.tab-pill.has-separator::before {
	content: '';
	position: absolute;
	left: -2px;
	top: 20%;
	bottom: 20%;
	width: 1px;
	background-color: var(--color-border);
}

.tab-pill.tab-shifting {
	transition: transform 200ms ease;
}

.tab-pill.tab-dragging {
	width: 0;
	min-width: 0;
	padding-inline: 0;
	gap: 0;
	opacity: 0;
	overflow: hidden;
}

/* split ghost overlay */
.split-ghost {
	position: absolute;
	pointer-events: none;
	z-index: 50;
	background-color: var(--color-primary);
	opacity: 0.12;
	border-radius: 6px;
	transition: all 150ms ease;
}

.split-ghost-left {
	top: 4px;
	left: 4px;
	bottom: 4px;
	width: calc(50% - 6px);
}

.split-ghost-right {
	top: 4px;
	right: 4px;
	bottom: 4px;
	width: calc(50% - 6px);
}

.split-ghost-top {
	top: 4px;
	left: 4px;
	right: 4px;
	height: calc(50% - 6px);
}

.split-ghost-bottom {
	bottom: 4px;
	left: 4px;
	right: 4px;
	height: calc(50% - 6px);
}

.split-ghost-center {
	inset: 4px;
}
</style>
